import { Link, useSearchParams } from 'react-router-dom';

import { AsyncBoundary, EmptyState, Icon } from '@kollez/design-system';
import { formatIndianDate } from '@kollez/shared';
import { useApplicationRun } from '@/lib/queries';

/**
 * /progress?applicationId=... — student's view of their WorkflowRun for one application.
 *
 * Step 91 rewrite: the earlier version was a direct port of the design's static Wipro
 * mockup with hardcoded stages, which would render fabricated progress to any student
 * who landed here from a bookmark or a deep link. No in-app link points to this route
 * (verified via grep) so the traffic is close to zero, but the trust-bomb severity of
 * "student sees fake stages" is high. This rewrite:
 *
 *   - Requires an `?applicationId=<id>` query param. Without it, renders an empty state
 *     pointing at /applications.
 *   - Fetches the real WorkflowRun via `useApplicationRun` (Step 27's endpoint).
 *   - Renders whatever the run actually has: current stage, history entries, auto-
 *     satisfied stages, status. No invented stage data, no invented scores.
 *   - When the run's Workflow.stages[] populates fully, iterates them for the full
 *     journey; otherwise falls back to the history entries.
 *
 * The design's rich "next / countdown / promises" UX from progress.html is deferred
 * to a follow-up Step once the underlying WorkflowRun carries the data those cards
 * need (promised-by dates, per-stage effort minutes). Rendering them from fabricated
 * data was the original bug.
 */
function prettyStage(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function StudentProgressPage() {
  const [params] = useSearchParams();
  const applicationId = params.get('applicationId');
  const q = useApplicationRun(applicationId ?? undefined);

  if (!applicationId) {
    return (
      <div className="stack">
        <div className="page-header">
          <div className="ph-text">
            <h1 className="h-page">Application progress</h1>
            <p className="subhead">Open any application to see its full workflow — every stage, what's next, and how long each step takes.</p>
          </div>
        </div>
        <EmptyState
          title="Pick an application first"
          hint="Progress pages are per-application — the URL needs ?applicationId=<id>. Head to your applications list to open one."
          action={<Link className="btn btn-primary" to="/applications">Open my applications</Link>}
        />
      </div>
    );
  }

  const run = q.data?.run ?? null;
  const workflow = run && typeof run.workflow === 'object' ? run.workflow : null;
  const stages = workflow?.stages ?? [];
  const currentStage = run?.currentStage;
  const currentIdx = stages.findIndex((s) => s.key === currentStage);

  return (
    <div className="stack" style={{ paddingBottom: 96 }}>
      <style>{`
        .jt { display: grid; gap: 0; }
        .jstage { display: grid; grid-template-columns: 34px minmax(0,1fr); gap: 14px; padding: 0 0 22px; position: relative; }
        .jstage::before { content: ""; position: absolute; left: 16px; top: 34px; bottom: 0; width: 2px; background: rgb(var(--line-tertiary)); }
        .jstage:last-child::before { display: none; }
        .jdot { width: 34px; height: 34px; border-radius: 50%; display: grid; place-items: center; border: 2px solid rgb(var(--line-secondary)); background: rgb(var(--bg-primary)); color: rgb(var(--ink-tertiary)); z-index: 1; }
        .jstage[data-s="done"] .jdot { background: rgb(var(--ink-success)); border-color: rgb(var(--ink-success)); color: #fff; }
        .jstage[data-s="now"] .jdot { border-color: rgb(var(--ink-accent)); color: rgb(var(--ink-accent)); box-shadow: 0 0 0 4px rgb(var(--ink-accent) / .15); }
        .jbody { display: grid; gap: 8px; padding-bottom: 4px; }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Application progress</p>
          <h1 className="h-page">{workflow?.name ?? 'Workflow run'}</h1>
          <p className="subhead">
            {run?.status === 'active' && currentStage && `Currently at: ${prettyStage(currentStage)}`}
            {run?.status === 'completed' && 'Completed'}
            {run?.status === 'abandoned' && 'Abandoned'}
            {run?.status === 'terminated' && 'Terminated'}
            {run?.status === 'flagged' && 'Flagged for review'}
          </p>
        </div>
      </div>

      <AsyncBoundary
        kind="detail"
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && !run}
        emptyTitle="No workflow run for this application"
        emptyHint="The application either predates the workflow feature or belongs to a different student."
        onRetry={() => void q.refetch()}
      >
        {run && (
          <section className="card card-pad grid gap-4">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <h2 className="h-section">Every step</h2>
              {run.autoSatisfiedStages.length > 0 && (
                <span className="helper">
                  {run.autoSatisfiedStages.length} auto-satisfied via badge{run.autoSatisfiedStages.length === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <div className="jt">
              {stages.length > 0 ? (
                stages.map((stage, idx) => {
                  const state: 'done' | 'now' | 'next' = idx < currentIdx ? 'done' : idx === currentIdx ? 'now' : 'next';
                  const auto = run.autoSatisfiedStages.includes(stage.key);
                  const hist = run.history.find((h) => h.toStage === stage.key);
                  return (
                    <div key={stage.key} className="jstage" data-s={state}>
                      <span className="jdot">
                        {state === 'done' && <Icon name="check" size={14} />}
                        {state === 'now' && <Icon name="dot" size={14} />}
                        {state === 'next' && <Icon name="circle" size={14} />}
                      </span>
                      <div className="jbody">
                        <div className="flex items-center gap-2 flex-wrap">
                          <b>{stage.label ?? stage.key}</b>
                          {state === 'now' && <span className="badge badge-info">Now</span>}
                          {auto && <span className="badge badge-success">Auto-satisfied</span>}
                        </div>
                        {hist && (
                          <p className="helper m-0">
                            Reached on {formatIndianDate(hist.at)}
                            {hist.note ? ` · ${hist.note}` : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                // Workflow.stages not populated on this run — fall back to history entries.
                run.history.map((h, i) => (
                  <div key={i} className="jstage" data-s={i === run.history.length - 1 ? 'now' : 'done'}>
                    <span className="jdot"><Icon name="check" size={14} /></span>
                    <div className="jbody">
                      <b>{prettyStage(h.toStage ?? h.fromStage ?? 'Stage')}</b>
                      <p className="helper m-0">
                        {formatIndianDate(h.at)}
                        {h.note ? ` · ${h.note}` : ''}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        )}
      </AsyncBoundary>

      <p className="helper">
        Progress data comes from the WorkflowRun tied to this application. To see other applications, open{' '}
        <Link className="link" to="/applications">your applications list</Link>.
      </p>
    </div>
  );
}
